import axios from 'axios';

import type * as api from '@/lib/api';
import { logger } from '@/lib/hooks/useLogger';

export const LS_ACCESS_TOKEN = 'oyg.accessToken';
export const LS_REFRESH_TOKEN = 'oyg.refreshToken';

/**
 * Clear the access and refresh tokens from local storage
 */
export const clearTokenStorage = (): void => {
    try {
        localStorage.removeItem(LS_ACCESS_TOKEN);
        localStorage.removeItem(LS_REFRESH_TOKEN);
    } catch (e) {
        logger.log.error('Error clearing tokens from local storage', e);
    }
};

export const setTokens = ({ accessToken, refreshToken }: { accessToken: string; refreshToken: string }): void => {
    try {
        localStorage.setItem(LS_ACCESS_TOKEN, accessToken);
        localStorage.setItem(LS_REFRESH_TOKEN, refreshToken);
    } catch (e) {
        logger.log.error('Error saving tokens to local storage', e);
    }
};

export const decodeToken = (token: string): Record<string, unknown> => {
    if (!token) {
        return {};
    }
    const [, base64Url] = token.split('.');
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            // biome-ignore lint/style/useTemplate: using a template here makes the code less readable
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
    );

    return JSON.parse(jsonPayload);
};

export const getTokenExpiry = (token: string): number => {
    try {
        let payloadBase64 = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
        const pad = payloadBase64.length % 4;
        if (pad === 1) {
            return 0;
        }
        if (pad) {
            payloadBase64 += new Array(5 - pad).join('=');
        }

        const jsonPayload = atob(payloadBase64);
        const payload = JSON.parse(jsonPayload);
        const { exp } = payload;
        return typeof exp === 'number' ? exp : 0;
    } catch (_e) {
        return 0;
    }
};

export const isTokenExpired = (token: string): boolean => {
    const expiryMs = getTokenExpiry(token) * 1000;
    return expiryMs < Date.now() + 300000;
};

const refreshLogin = async (refreshToken: string): Promise<api.Token> => {
    const { data: tokens } = await axios.post<api.Token>('/api/login/refresh', null, {
        headers: {
            Authorization: `bearer ${refreshToken}`,
        },
    });
    return tokens;
};

export const getToken = async (): Promise<string | null> => {
    const token = localStorage.getItem(LS_ACCESS_TOKEN) ?? '';
    if (!token) {
        return null;
    }
    const parsed = decodeToken(token);
    const { exp } = parsed;

    // Unexpected token contents, clear it out
    if (typeof exp !== 'number') {
        clearTokenStorage();
        return null;
    }

    // Token is not expired, return it
    if (exp * 1000 + 5000 >= Date.now()) {
        return token;
    }

    const refreshToken = localStorage.getItem(LS_REFRESH_TOKEN) ?? '';
    const refreshParsed = decodeToken(refreshToken);
    const { exp: refreshExp } = refreshParsed;

    // Unexpected token contents
    if (typeof refreshExp !== 'number') {
        clearTokenStorage();
        return null;
    }

    // Refresh token is expired, clear it out
    if (refreshExp * 1000 + 5000 < Date.now()) {
        clearTokenStorage();
        return null;
    }

    // Refresh the token
    try {
        const result = await refreshLogin(refreshToken);
        localStorage.setItem(LS_ACCESS_TOKEN, result.access_token);
        localStorage.setItem(LS_REFRESH_TOKEN, result.refresh_token);
        return result.access_token;
    } catch (_e) {
        clearTokenStorage();
        return null;
    }
};
